import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Theme } from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import React, { FunctionComponent, useState } from 'react';
import { IpfModule } from "../..";
import { EventConstants } from "../../EventConstants";
import { TYPES } from "../../ioc/types";
import { SelectedTier, Tier } from "../../models/app";
import { ApiResponse } from "../../models/http";
import { AjaxService, UrlService, UserTierService } from "../../services";
import { useInject } from "../hooks/useInject";

/**
 * Select tier dialog options interface.
 */
export interface SelectTierDialogOptions {

	/**
	 * Open dialog handler that gets populated after rendering.
	 * @param selectedTier Currently selected tier.
	 * @param rootTier Minimum tier that cannot be changed.
	 */
	open?: (selectedTier: SelectedTier | undefined, rootTier: SelectedTier | undefined) => void;

	/**
	 * On tier selected handler. Called when a tier has been selected.
	 * @param tier The tier selected.
	 */
	onSelect?: (tier: SelectedTier) => void;

	/**
	 * On tier selection cancelled. Called when the selection prompt is closed without selecting a new tier.
	 */
	onCancel?: () => boolean;

	/**
	 * Currently selected tier. Prepopulates the tier selection controls.
	 */
	selectedTier?: SelectedTier;

	/**
	 * Determines whether tier 3 broker is required or not.
	 */
	requiresBroker?: boolean;

	/**
	 * Determines whether tier 2 group is required or not.
	 */
	requiresGroup?: boolean;

	/**
	 * Determines whether the tier selection component should be open by default.
	 */
	autoOpen?: boolean;
}

/**
 * Select tier dialog component state interface.
 */
interface SelectTierDialogState {
	open: boolean;

	group: Tier;
	branch: Tier;
	broker: Tier;

	groupOptions: Array<Tier>;
	branchOptions: Array<Tier>;
	brokerOptions: Array<Tier>;

	groupOpen: boolean | undefined;
	branchOpen: boolean | undefined;
	brokerOpen: boolean | undefined;

	groupEnabled: boolean;
	branchEnabled: boolean;
	brokerEnabled: boolean;

	groupHidden: boolean;
	branchHidden: boolean;
	brokerHidden: boolean;

	groupLabel: string;
	branchLabel: string;
	brokerLabel: string;
}

// create custom styles for formControl class to be applied to the tier combo box dropdowns
const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		formControl: {
			marginTop: theme.spacing(3),
			minWidth: 400,
		}
	})
);

/**
 * Gets an unset tier with no values assigned.
 */
const unsetTier = () => {
	return {} as Tier;
};

/**
 * Gets the list of tier 1 groups to display in the combo box.
 * @param urlService URL service to build URL.
 * @param ajaxService AJAX service to call the server and get the data.
 * @param search An optional search string, for combo box text filtering.
 * @returns Promise of an API response containing an array of tiers.
 */
const getGroups = (urlService: UrlService, ajaxService: AjaxService, search?: string) => {

	return getTiers(urlService, ajaxService, "groups", search);
};

/**
 * Gets the list of tier 2 branches to display in the combo box.
 * @param urlService URL service to build URL.
 * @param ajaxService AJAX service to call the server and get the data.
 * @param search An optional search string, for combo box text filtering.
 * @returns Promise of an API response containing an array of tiers.
 */
const getBranches = (urlService: UrlService, ajaxService: AjaxService, groupId: string, search?: string) => {

	return getTiers(urlService, ajaxService, `groups/${groupId}/branches`, search);
};

/**
 * Gets the list of tier 3 brokers to display in the combo box.
 * @param urlService URL service to build URL.
 * @param ajaxService AJAX service to call the server and get the data.
 * @param search An optional search string, for combo box text filtering.
 * @returns Promise of an API response containing an array of tiers.
 */
const getBrokers = async (urlService: UrlService, ajaxService: AjaxService, groupId: string, branchId: string, search?: string) => {

	return getTiers(urlService, ajaxService, `groups/${groupId}/branches/${branchId}/brokers`, search);
};

/**
 * Gets a collection of tiers.
 * @param urlService URL service to build URL.
 * @param ajaxService AJAX service to call the server and get the data.
 * @param action The tier specific action path to call to get the data.
 * @param search An optional search string, for combo box text filtering.
 * @returns Promise of an API response containing an array of tiers.
 */
const getTiers = (urlService: UrlService, ajaxService: AjaxService, action: string, search?: string) => {

	let data: { search: string } | undefined;
	if (search) {
		data = { search };
	}

	// build the action URL
	const url = urlService.apiAction({
		controller: "tier",
		action,
		data
	});

	// request the tiers
	return ajaxService.request<ApiResponse<Array<Tier>>>({
		url
	});
};

/**
 * Select tier dialog, returns a prompt with tier selection controls.
 * @param props Configuration properties, enabling preselection and event handling.
 * @see SelectTierDialogOptions
 * @see Dialog
 * @see Autocomplete
 */
export const SelectTierDialog: FunctionComponent<{ options: SelectTierDialogOptions }> = (props: { options: SelectTierDialogOptions }) => {

	// get dependencies, user tier and page tier
	const userTierService = useInject<UserTierService>(TYPES.UserTierService);
	const urlService = useInject<UrlService>(TYPES.UrlService);
	const ajaxService = useInject<AjaxService>(TYPES.AjaxService);
	const ipfModule = useInject<IpfModule>(TYPES.IpfModule);
	const userTier = ipfModule.context.userTier;
	const pageTier = userTierService.getPageTier();

	// setup initial statye
	const [state, setState] = useState({
		open: props.options.autoOpen ?? false,

		group: unsetTier(),
		branch: unsetTier(),
		broker: unsetTier(),

		groupOptions: [] as Tier[],
		branchOptions: [] as Tier[],
		brokerOptions: [] as Tier[],

		groupEnabled: true,
		branchEnabled: false,
		brokerEnabled: false,

		groupHidden: !userTierService.isAllowedToChangeGroup(),
		branchHidden: !userTierService.isAllowedToChangeBranch(),
		brokerHidden: !userTierService.isAllowedToChangeBroker(),

		groupLabel: userTier?.group?.groupLabel || "Group",
		branchLabel: userTier?.group?.branchLabel || "Broker",
		brokerLabel: userTier?.group?.brokerLabel || "Broker Branch"
	} as SelectTierDialogState);

	const classes = useStyles();

	// refresh state handler
	const refreshState = () => {
		setState({ ...state });
	};

	// if auto open by default, setup state, specific to the current page tier
	if (props.options.autoOpen) {

		let updateState: boolean | undefined;

		if (!state.group.id && pageTier?.group) {
			state.group = pageTier.group;
			state.groupEnabled = false;
			state.branchEnabled = true;
			updateState = true;
		}
		if (!state.branch.id && pageTier?.branch) {
			state.branch = pageTier.branch;
			state.branchEnabled = false;
			state.brokerEnabled = true;
			updateState = true;
		}
		if (!state.broker.id && pageTier?.broker) {
			state.broker = pageTier.broker;
			state.brokerEnabled = false;
			updateState = true;
		}
		if (updateState) {
			refreshState();
		}
	}

	// cancel button handler, closes the dialog
	const cancelHandler = () => {

		if (!props.options.onCancel || props.options.onCancel()) {
			state.open = false;
			refreshState();
		}
	};

	// open handler, enables opening the dialog on demand with a tier selected and a minimum root tier
	const openHandler = (selectedTier: SelectedTier | undefined, rootTier: SelectedTier | undefined) => {

		// apply selected tier to state
		if (selectedTier) {

			if (selectedTier.group?.id) {
				state.group = selectedTier.group;
				state.branchEnabled = true;
			}
			else {
				state.group = unsetTier();
				state.branchEnabled = false;
			}

			if (selectedTier.branch?.id) {
				state.branch = selectedTier.branch;
				state.brokerEnabled = true;
			}
			else {
				state.branch = unsetTier();
				state.brokerEnabled = false;
			}

			if (selectedTier.broker?.id) {
				state.broker = selectedTier.broker;
			}
			else {
				state.broker = unsetTier();
			}
		}

		// use root tier to apply non-editable tiers to state
		if (rootTier) {

			if (rootTier.group?.id) {
				state.group = rootTier.group;
				state.groupEnabled = false;
				state.branchEnabled = true;
			}
			else {
				state.groupEnabled = true;
			}
			if (rootTier.branch?.id) {
				state.branch = rootTier.branch;
				state.branchEnabled = false;
				state.brokerEnabled = true;
			}
			else {
				state.branchEnabled = state.group?.id != undefined;
			}
			if (rootTier.broker?.id) {
				state.broker = rootTier.broker;
				state.brokerEnabled = false;
			}
			else {
				state.brokerEnabled = state.branch?.id != undefined;
			}
		}

		state.open = true;
		refreshState();
	};

	// expose the open handler
	props.options.open = openHandler;

	// tier 1 group selected handler
	const groupSelected = (event: React.ChangeEvent<{}>, value: Tier | null) => {

		const selectedGroup = value || unsetTier();
		state.group = selectedGroup;
		state.broker = unsetTier();
		state.branch = unsetTier();
		setEnabledState(selectedGroup, state.branch);
	};

	// tier 2 branch selected handler
	const branchSelected = (event: React.ChangeEvent<{}>, value: Tier | null) => {
		const selectedBranch = value || unsetTier();
		state.branch = selectedBranch;
		state.broker = unsetTier();
		setEnabledState(state.group, selectedBranch);
	};

	// tier 3 broker selected handler
	const brokerSelected = (event: React.ChangeEvent<{}>, value: Tier | null) => {
		const selectedBroker = value || unsetTier();
		state.broker = selectedBroker;
		refreshState();
	};

	// function refreshes the enabled state of the tier combo boxes
	const setEnabledState = (selectedGroup: Tier, selectedBranch: Tier) => {

		const groupId = selectedGroup?.id;
		const hasGroup = groupId !== undefined && groupId.length > 0;

		const branchId = selectedBranch?.id;
		const hasBranch = branchId !== undefined && branchId.length > 0;

		if (state.branchEnabled !== hasGroup) {
			state.branchEnabled = hasGroup;
			if (!hasGroup && state.branch) {
				state.branch = unsetTier();
				if (state.broker)
					state.broker = unsetTier();
			}
		}

		if (state.branchEnabled !== hasGroup || state.brokerEnabled !== hasBranch) {
			state.brokerEnabled = hasGroup && hasBranch;
			if (!hasBranch && state.branch)
				state.broker = unsetTier();
		}

		refreshState();
	};

	// button click handler for when a tier has been chosen and is confirmed for selection
	const selectTierHandler = () => {

		// check required properties are set
		if (props.options.requiresBroker && !state.broker.id) {
			return;
		}

		else if (props.options.requiresGroup && !state.group.id) {
			return;
		}

		if (props.options.onSelect) {

			// call provided on select handler
			props.options.onSelect({
				group: state.group.id ? state.group : undefined,
				branch: state.branch.id ? state.branch : undefined,
				broker: state.broker.id ? state.broker : undefined
			} as SelectedTier);
		}

		state.open = false;
		refreshState();
	};

	// create cancel button if applicable
	const cancelButton = !(props.options.requiresBroker || props.options.requiresGroup) || props.options.onCancel ? <Button onClick={cancelHandler} color="secondary" variant="contained">
		Cancel
	</Button> : null;

	// set message to display
	const message = props.options.requiresBroker ? "Please select a broker branch." : props.options.requiresGroup ? "Please select a group." : "Select a tier to filter site content.";

	// output dialog with auto complete combo controls
	return (
        <Dialog
            open={state.open}
            onClose={cancelHandler}
            aria-labelledby="form-dialog-title"
            className="tier-dialog">
			<DialogTitle id="form-dialog-title">Select Tier</DialogTitle>
			<DialogContent>
				<DialogContentText>
					{message}
				</DialogContentText>
				<Autocomplete					
					open={state.groupOpen}
					hidden={state.groupHidden}
					disabled={state.groupEnabled !== true}
					options={state.groupOptions}
					getOptionLabel={option => option.name ?? ""}
					className={classes.formControl}
					value={state.group}
					renderInput={params => <TextField {...params} label={state.groupLabel} variant="outlined" />}
					onChange={groupSelected}
					onOpen={async () => {
						const response = await getGroups(urlService, ajaxService);
						if (response?.isValid === true) {
							state.groupOptions = response.result;
							refreshState();
						}
					}}
					onInputChange={async (event, value, reason) => {
						if (reason === EventConstants.input) {
							const response = await getGroups(urlService, ajaxService, value);
							if (response?.isValid === true) {
								state.groupOptions = response.result;
								refreshState();
							}
						}
					}}
				/>
				<Autocomplete
					open={state.branchOpen}
					hidden={state.branchHidden}
					disabled={state.branchEnabled !== true}
					options={state.branchOptions}
					getOptionLabel={option => option.name ?? ""}
					className={classes.formControl}
					value={state.branch}
					renderInput={params => <TextField {...params} label={state.branchLabel} variant="outlined" />}
					onChange={branchSelected}
					onOpen={async () => {
						if (state.group) {
							const response = await getBranches(urlService, ajaxService, state.group.id);
							if (response?.isValid === true) {
								state.branchOptions = response.result;
								refreshState();
							}
						}
					}}
					onInputChange={async (event, value, reason) => {
						if (reason === EventConstants.input) {
							const response = await getBranches(urlService, ajaxService, state.group.id, value);
							if (response?.isValid === true) {
								state.branchOptions = response.result;
								refreshState();
							}
						}
					}}
				/>
				<Autocomplete
					open={state.brokerOpen}
					hidden={state.brokerHidden}
					disabled={state.brokerEnabled !== true}
					options={state.brokerOptions}
					getOptionLabel={option => option.name ?? ""}
					className={classes.formControl}
					value={state.broker}
					renderInput={params => <TextField {...params} label={state.brokerLabel} variant="outlined" />}
					onChange={brokerSelected}
					onOpen={async () => {
						if (state.group && state.branch) {
							const response = await getBrokers(urlService, ajaxService, state.group.id, state.branch.id);
							if (response?.isValid === true) {
								state.brokerOptions = response.result;
								refreshState();
							}
						}
					}}
					onInputChange={async (event, value, reason) => {
						if (reason === EventConstants.input) {
							const response = await getBrokers(urlService, ajaxService, state.group.id, state.branch.id, value);
							if (response?.isValid === true) {
								state.brokerOptions = response.result;
								refreshState();
							}
						}
					}}
				/>
			</DialogContent>
			<DialogActions className="mt-3">
				{cancelButton}
				<Button onClick={selectTierHandler} color="primary" variant="contained">
					Select
				</Button>
			</DialogActions>
		</Dialog>
    );
}